import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogContent,
  Grid,
  IconButton,
  Snackbar,
  Typography,
  Chip,
} from "@mui/material";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import HttpService from "../services/HttpService";
import Labels from "../labels/Labels";
import CloseIcon from "@mui/icons-material/Close";
import SaveIcon from "@mui/icons-material/Save";
import ToggleOffOutlinedIcon from "@mui/icons-material/ToggleOffOutlined";
import ToggleOnIcon from "@mui/icons-material/ToggleOn";
import DeleteIcon from "@mui/icons-material/Delete";
import ListAltIcon from "@mui/icons-material/ListAlt";
import RuleIcon from "@mui/icons-material/Rule";
import AddSpendRule from "../components/AddSpendRule";
import ManageMccCodes from "../components/ManageMccCodes";
import React, { useState } from "react";
import RenderOnRole from "../components/RenderOnRole";
import ButtonWithIcon from "../components/Button";
import RenderIfNotOnRole from "../components/RenderIfNotOnRole";
import UserService from "../services/UserService";

async function fetchSpendRules() {
  const response = await HttpService.getAxiosClient().get(
    window.API_URL + `/rules/spendrules`
  );
  return response;
}

export default function SpendRulesConfig() {
  const [previousValues, setPreviousValues] = useState({});
  const [feedback, setFeedback] = useState({
    open: false,
    type: "error",
    message: "",
  });

  const [mccDlgInfo, setMccDlgInfo] = useState();
  const queryClient = useQueryClient();

  const updateSpendRuleMutation = useMutation(
    (values) => {
      return HttpService.getAxiosClient().put(
        window.API_URL + `/rules/spendrules/${values.id}`,
        values.row
      );
    },
    {
      onSuccess: (data, value) => {
        setPreviousValues((prevPreviousValues) => {
          const newPreviousValues = { ...prevPreviousValues };
          delete newPreviousValues[value.id];
          return newPreviousValues;
        });
        queryClient.invalidateQueries(["spendRules"]);
        setFeedback({
          open: true,
          type: "success",
          message: "Spend rule updated successfully",
        });
      },
      onError: (error) => {
        setFeedback({
          open: true,
          type: "error",
          message: "Error updating the spend rule",
        });
      },
    }
  );

  const deleteSpendRuleMutation = useMutation(
    (values) => {
      return HttpService.getAxiosClient().delete(
        window.API_URL + `/rules/spendrules/${values.id}`
      );
    },
    {
      onSuccess: (dataResponse, value) => {
        queryClient.invalidateQueries(["spendRules"]);
        setFeedback({
          open: true,
          type: "success",
          message: "Spend rule deleted successfully",
        });
      },
      onError: (error) => {
        setFeedback({
          open: true,
          type: "error",
          message: "Error deleting the spend rule",
        });
      },
    }
  );

  const confirmSpendRuleMutation = useMutation(
    (idSpendRule) => {
      return HttpService.getAxiosClient().post(
        window.API_URL + `/rules/spendrules/${idSpendRule}/confirm`
      );
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["spendRules"] });
        setFeedback({
          open: true,
          type: "success",
          message: "The request was verified successfully",
          onClose: () => {
            setFeedback({ open: false });
          },
        });
      },
      onError: (err) => {
        const msg = err?.response?.data ? err.response.data : err.message;
        setFeedback({
          open: true,
          type: "error",
          message: msg,
        });
      },
    }
  );

  const cancelSpendRuleMutation = useMutation(
    (idSpendRule) => {
      return HttpService.getAxiosClient().post(
        window.API_URL + `/rules/spendrules/${idSpendRule}/cancel`
      );
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["spendRules"] });
        setFeedback({
          open: true,
          type: "success",
          message: "The request was verified succesfully",
          onClose: () => {
            setFeedback({ open: false });
          },
        });
      },
      onError: (err) => {
        const msg = err?.response?.data ? err.response.data : err.message;
        setFeedback({
          open: true,
          type: "error",
          message: msg,
        });
      },
    }
  );

  const handleEditCellChange = (params, id, field, oldValue, newValue) => {
    if (newValue !== undefined && oldValue !== newValue) {
      setPreviousValues((prevPreviousValues) => ({
        ...prevPreviousValues,
        [id]: { ...prevPreviousValues[id], [field]: oldValue },
      }));
    }
  };

  const clearEditedRow = (params) => {
    params.row[params.field] = previousValues[params.id][params.field];
    setPreviousValues((prevPreviousValues) => {
      const newPreviousValues = { ...prevPreviousValues };
      delete newPreviousValues[params.id];
      return newPreviousValues;
    });
  };

  const onVerifyButtonClick = (e, params) => {
    confirmSpendRuleMutation.mutate(params.id);
  };

  const onCancelButtonClick = (e, params) => {
    cancelSpendRuleMutation.mutate(params.id);
  };

  const columns = [
    {
      field: "employerName",
      headerName: Labels.getLabel(
        "spendRulesConfig.column.employerName",
        "Employer"
      ),
      width: 200,
    },
    {
      field: "ruleName",
      headerName: Labels.getLabel(
        "spendRulesConfig.column.ruleName",
        "Rule Name"
      ),
      width: 200,
    },

    {
      field: "ruleAccessType",
      headerName: Labels.getLabel(
        "spendRulesConfig.column.accessType",
        "Access Type"
      ),
      width: 120,
      editable: false,
      type: "singleSelect",
      valueOptions: ["BLOCK", "ALLOW"],
    },
    {
      field: "ruleTargetName",
      headerName: Labels.getLabel(
        "spendRulesConfig.column.ruleTargetName",
        "Rule Target Name"
      ),
      width: 120,
      editable: false,
    },
    {
      field: "ruleTargetType",
      headerName: Labels.getLabel(
        "spendRulesConfig.column.targetType",
        "Target Type"
      ),
      width: 120,
      editable: false,
      type: "singleSelect",
      valueOptions: ["PROGRAM"],
    },

    {
      field: "mccList",
      headerName: Labels.getLabel(
        "spendRulesConfig.column.mccList",
        "MCC List"
      ),
      width: 350,
      renderCell: (params) => {
        return (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
            {params.value &&
              params.value.map((mcc, index) => (
                <Chip
                  key={index}
                  label={
                    mcc.code && mcc.description
                      ? `${mcc.code} - ${mcc.description}`
                      : mcc
                  }
                  size="small"
                />
              ))}
          </Box>
        );
      },
    },
    {
      field: "changeRequest",
      headerName: Labels.getLabel(
        "spendRulesConfig.column.changeRequest",
        "Change Request"
      ),
      width: 350,
      renderCell: (params) => {
        return (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
            {params?.value?.mccList &&
              params?.value?.mccList.map((mcc, index) => (
                <Chip
                  key={index}
                  label={
                    mcc.code && mcc.description
                      ? `${mcc.code} - ${mcc.description}`
                      : mcc
                  }
                  size="small"
                />
              ))}

            {params?.value?.newStatus && (
              <Chip
                label={`New Status - ${params.value.newStatus}`}
                size="small"
              />
            )}
          </Box>
        );
      },
    },
    {
      field: "status",
      headerName: Labels.getLabel("spendRulesConfig.column.status", "Status"),
      width: 165,
      renderCell: (params) => {
        return (
          // you will find row info in params
          <div>
            <Typography
              sx={{ display: "block", fontWeight: "bold", fontSize: 12, mb: 0 }}
              gutterBottom
            >
              {params.row.status}
            </Typography>
          </div>
        );
      },
    },
    {
      field: "action",
      headerName: Labels.getLabel("spendRulesConfig.column.action", "Action"),
      width: 150,
      renderCell: (params) => {
        return (
          <div key={"action." + params.id} style={{ display: "flex" }}>
            <RenderOnRole roles={["sr-confirm"]}>
              <ButtonWithIcon
                hidden={
                  !(
                    (params.row.status === "PENDING_CONFIRMATION" ||
                      params.row.status === "CHANGE_REQUESTED") &&
                    params.row.allowApprove === "true"
                  )
                }
                onClick={onVerifyButtonClick}
                color="green"
                tooltip="Click to verify and accept the request"
                icon="confirm"
                record={params.row}
                isLoading={confirmSpendRuleMutation.isLoading}
                disabled={cancelSpendRuleMutation.isLoading}
              />
              <ButtonWithIcon
                hidden={
                  !(
                    (params.row.status === "PENDING_CONFIRMATION" ||
                      params.row.status === "CHANGE_REQUESTED") &&
                    params.row.allowApprove === "true"
                  )
                }
                onClick={onCancelButtonClick}
                color="red"
                tooltip="Click to cancel and close the request"
                icon="deny"
                record={params.row}
                isLoading={cancelSpendRuleMutation.isLoading}
                disabled={confirmSpendRuleMutation.isLoading}
              />
            </RenderOnRole>

            {params.id in previousValues ? (
              <IconButton
                key={"clear." + params.id}
                onClick={() => {
                  clearEditedRow(params);
                }}
              >
                <CloseIcon />
              </IconButton>
            ) : (
              <>
                {params.row.status !== "PENDING_CONFIRMATION" &&
                  params.row.status !== "CHANGE_REQUESTED" && (
                    <>
                      <IconButton
                        disabled={params.id in previousValues}
                        key={"manageMcc." + params.id}
                        onClick={() => {
                          setMccDlgInfo({ ...params });
                        }}
                      >
                        <ListAltIcon />
                      </IconButton>
                      <IconButton
                        key={"delete." + params.id}
                        onClick={() => {
                          deleteSpendRuleMutation.mutate({ ...params });
                        }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </>
                  )}
              </>
            )}
          </div>
        );
      },
    },
  ];

  const { data, isLoading } = useQuery(["spendRules"], fetchSpendRules);
  const [addRuleDlg, setAddRuleDlg] = useState(false);

  const addRuleDlgFunct = () => {
    return (
      <Dialog open={addRuleDlg} fullWidth onClose={() => {}}>
        <Box
          sx={{
            backgroundColor: "primary.main",
            px: 3,
            minHeight: 52,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <RuleIcon sx={{ color: "white" }} />
            <Typography variant="h6" sx={{ color: "white" }}>
              {Labels.getLabel(
                "spendRulesConfig.AddSpendRule",
                "Add Spend Rule"
              )}
            </Typography>
          </Box>
          <IconButton
            onClick={() => {
              setAddRuleDlg(false);
            }}
            sx={{ color: "white" }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <DialogContent sx={{ paddingTop: 3 }}>
          <AddSpendRule
            handleClose={() => {
              setAddRuleDlg(false);
            }}
          />
        </DialogContent>
      </Dialog>
    );
  };

  const manageMccDlgFunct = () => {
    return (
      <Dialog
        maxWidth="md"
        open={mccDlgInfo !== undefined}
        fullWidth
        onClose={() => {}}
      >
        <Box
          sx={{
            backgroundColor: "primary.main",
            px: 3,
            minHeight: 52,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <ListAltIcon sx={{ color: "white" }} />
            <Typography variant="h6" sx={{ color: "white" }}>
              {Labels.getLabel(
                "spendRulesConfig.ManageMccCodes",
                "Manage MCC Codes"
              )}
            </Typography>
          </Box>
          <IconButton
            onClick={() => {
              setMccDlgInfo();
            }}
            sx={{ color: "white" }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <DialogContent sx={{ paddingTop: 3 }}>
          <ManageMccCodes
            ruleInfo={mccDlgInfo}
            handleClose={() => {
              setMccDlgInfo();
            }}
          />
        </DialogContent>
      </Dialog>
    );
  };

  return (
    <Box sx={{ width: "100%", height: "100%" }}>
      {isLoading ? (
        ""
      ) : (
        <Grid container p={0} m={0} spacing={0}>
          <Grid item xs={12}>
            <Box display="flex">
              <Typography variant="h5">Spend Rules</Typography>
              <Button
                variant="outlined"
                size="small"
                onClick={() => setAddRuleDlg(true)}
              >
                Add Rule
              </Button>
            </Box>
          </Grid>
          <Grid item xs={12} sx={{ width: "100%", height: "80vh" }}>
            <DataGrid
              key="spendRulesGrid"
              rows={data.data}
              columns={columns}
              getRowHeight={() => "auto"}
              density="compact"
              onCellEditStop={(params, event) =>
                handleEditCellChange(
                  params,
                  params.id,
                  params.field,
                  params.value,
                  event?.target?.value
                )
              }
              disableDensitySelector
              components={{ Toolbar: GridToolbar }}
              componentsProps={{
                toolbar: {
                  showQuickFilter: true,
                  quickFilterProps: { debounceMs: 500 },
                },
              }}
            />
          </Grid>
        </Grid>
      )}
      {addRuleDlg ? addRuleDlgFunct() : ""}
      {mccDlgInfo !== undefined ? manageMccDlgFunct() : ""}
      <Snackbar
        open={feedback.open}
        autoHideDuration={6000}
        onClose={() => setFeedback({ ...feedback, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setFeedback({ ...feedback, open: false })}
          severity={feedback.type}
          sx={{ width: "100%" }}
        >
          {feedback.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
